import {
    Box,
    Container,
    Typography,
    Card,
    Button,
    Alert,
    CircularProgress
} from '@mui/material';
import { useUser } from '../../providers/user-provider';
import { useResendVerification } from '../../react-query/auth-queries';
import { useState } from 'react';
import { brand } from '../../shared-theme/themePrimitives';
import { MarkEmailUnread } from '@mui/icons-material';

export default function CheckEmailPage() {
    const { user, logout } = useUser();
    const { mutateAsync: resendVerification, isPending } = useResendVerification();
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleResend = async () => {
        if (!user?.email) return;
        setError('');
        try {
            await resendVerification(user.email);
            setSent(true);
        } catch (err: any) {
            console.error("Resend verification error:", err);
            setError(err.response?.data?.message || 'Failed to resend verification email');
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(ellipse at 50% -20%, rgba(210, 225, 255, 0.5) 0%, rgba(255,255,255,0) 70%)',
                bgcolor: '#FAFAFA',
                py: 4,
                px: 2
            }}
        >
            <Container maxWidth="sm">
                <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box 
                        sx={{ 
                            width: 64, 
                            height: 64, 
                            bgcolor: 'white', 
                            borderRadius: '16px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            mb: 3,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                            color: brand[500]
                        }}
                    >
                        <MarkEmailUnread sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="h3" component="h1" gutterBottom fontWeight={800} sx={{ color: '#0F172A', fontSize: { xs: '1.75rem', md: '2rem' }, textAlign: 'center' }}>
                        Verify your email
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 400 }}>
                        We've sent a verification link to <Box component="span" fontWeight="bold" color="text.primary">{user?.email}</Box>. 
                        Please check your inbox and click the link to verify your account.
                    </Typography>
                </Box>

                <Card 
                    elevation={0} 
                    sx={{ 
                        p: { xs: 3, sm: 5 }, 
                        borderRadius: '24px',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
                    }}
                >
                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
                    )}
                    
                    {sent ? (
                        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                            Verification email sent successfully! Please check your inbox (and spam folder).
                        </Alert>
                    ) : (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Didn't receive the email?
                            </Typography>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleResend}
                                disabled={isPending}
                                sx={{ height: 48, borderRadius: '12px', mb: 2 }}
                            >
                                {isPending ? <CircularProgress size={24} /> : 'Resend Verification Email'}
                            </Button>
                        </Box>
                    )}

                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button 
                            color="inherit" 
                            onClick={() => logout()}
                            sx={{ textTransform: 'none' }}
                        >
                            Log out
                        </Button>
                    </Box>
                </Card>
            </Container>
        </Box>
    );
}
